import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { canAccessRoute } from '@logisticslash/shared';
import VerifyEmailPage from '@/pages/VerifyEmailPage';

export function RequireAuth({ children, allowUnverified = false }) {
  const { user, ready, bootError, refresh } = useAuth();
  const location = useLocation();

  // Wait for AuthProvider Firebase boot — never redirect mid-restore (reload logout bug).
  if (!ready) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-sm">Opening your workspace…</p>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <p className="text-destructive">{bootError}</p>
          <button
            type="button"
            className="text-primary hover:underline text-sm"
            onClick={() => void refresh()}
          >
            Retry
          </button>
          <div>
            <a href="/login" className="text-muted-foreground hover:underline text-sm">
              Back to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (
    !allowUnverified &&
    user.role !== 'super_admin' &&
    user.emailVerified === false
  ) {
    return <VerifyEmailPage />;
  }

  const path = location.pathname || '/';

  if (user.role === 'super_admin' && !path.startsWith('/super')) {
    return <Navigate to="/super" replace />;
  }

  if (user.role !== 'super_admin' && path.startsWith('/super')) {
    return <Navigate to="/" replace />;
  }

  if (!canAccessRoute(user.role, path)) {
    return <Navigate to={user.role === 'super_admin' ? '/super' : '/'} replace />;
  }

  return children;
}
